import React, { useCallback, useState } from "react";
import { RootStackParamList } from "../../types/navigationParams";
import { StyleSheet, View } from "react-native";
import { Button, SegmentedButtons, useTheme } from "react-native-paper";
import DetailsDisplay from "../../components/DetailsDisplay";
import { HeaderBackButton } from "@react-navigation/elements";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { update } from "../../services/firestoreServices";
import { useScreenSize } from "../../hooks/useScreenSize";
import ModalScreenWrapper from "../../components/ModalScreenWrapper";
import { serverTimestamp } from "../../firebase";
import { getStatusAt } from "../../utils/utils";
import TaskActivityLogs from "./components/TaskActivityLogs";
import { updateTimestampAction } from "../../redux/slices/authSlice";

type Props = NativeStackScreenProps<RootStackParamList, "DetailsScreen">;

export default function DetailsScreen({ route, navigation }: Props) {
  const { data, collection } = route.params;
  const config = useAppSelector((state) => state.auth.config);
  const state = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { LARGE_SCREEN } = useScreenSize();
  const fields = config.database.find(
    (c) => c.name === collection.name,
  )?.fields;
  const activityLogsConfig = config.database.find(
    (c) => c.name === collection.name,
  )?.activityLogs;
  const boardScreen = config.screens.find(
    (screen) =>
      screen.type === "board" &&
      screen.collection === collection.name &&
      screen.roles.includes(state.user.role),
  );
  const quickStatus = boardScreen?.quickStatus;
  const [localData, setLocalData] = useState(data);
  const roleQuickFieldName =
    quickStatus?.fieldNameByRole?.[state.user.role] ??
    quickStatus?.fieldName ??
    "status";
  const quickStatusField = fields?.find(
    (field) => field.name === roleQuickFieldName,
  );
  const editableValuesFromField = React.useMemo<
    (string | number | boolean)[]
  >(() => {
    if (!quickStatusField) {
      return [];
    }

    if (quickStatusField.statusOptions?.length) {
      return quickStatusField.statusOptions
        .filter((option) => option.editableBy.includes(state.user.role))
        .map((option) => option.value);
    }

    if (
      quickStatusField.type === "dropdown" &&
      Array.isArray(quickStatusField.options)
    ) {
      return quickStatusField.options;
    }

    if (quickStatusField.type === "booleanCheckbox") {
      return [false, true];
    }

    return [];
  }, [quickStatusField, state.user.role]);
  const roleQuickStatuses = quickStatus?.statusesByRole?.[state.user.role];
  const quickStatusOptions = (
    roleQuickStatuses?.length ? roleQuickStatuses : editableValuesFromField
  )
    .filter(
      (value, index, arr) =>
        arr.findIndex((item) => String(item) === String(value)) === index,
    )
    .filter((value) =>
      roleQuickFieldName === "status"
        ? (config.statuses ?? []).includes(String(value))
        : true,
    );
  const normalizedQuickCurrentValue =
    typeof localData[roleQuickFieldName] !== "undefined" &&
    quickStatusOptions.some(
      (optionValue) =>
        String(optionValue) === String(localData[roleQuickFieldName]),
    )
      ? localData[roleQuickFieldName]
      : quickStatusField?.defaultValue;
  const quickFieldRoles =
    quickStatusField?.formEdit || quickStatusField?.form || [];
  const canEditByStatusOptions = !!quickStatusField?.statusOptions?.some(
    (option) => option.editableBy.includes(state.user.role),
  );
  const quickStatusEnabled =
    !!quickStatus &&
    !!quickStatusField &&
    (quickFieldRoles.includes(state.user.role) || canEditByStatusOptions) &&
    quickStatusOptions.length > 0;
  const shouldShowActivityLogs =
    !!activityLogsConfig &&
    !!localData?.id &&
    activityLogsConfig.roles.includes(state.user.role);

  console.log(localData);

  const onPressEdit = useCallback(() => {
    navigation.replace("FormScreen", {
      collection: collection.name,
      item: localData,
    });
  }, [collection.name, localData, navigation]);

  const handleFieldChange = (fieldName: string, newValue: any) => {
    const updatedData = { ...localData, [fieldName]: newValue };
    setLocalData(updatedData);
    const isStatusChanged =
      fieldName === "status" &&
      typeof newValue === "string" &&
      localData.status !== newValue;
    const payload = isStatusChanged
      ? {
          [fieldName]: newValue,
          statusChangedAt: serverTimestamp(),
          [getStatusAt(newValue)]: serverTimestamp(),
        }
      : { [fieldName]: newValue };

    // Save to Firestore immediately
    update(collection.name, localData.id, payload)
      .then(() => {
        dispatch(updateTimestampAction());
        console.log("Field updated successfully");
      })
      .catch((error) => {
        console.error("Failed to update field:", error);
      });
  };

  React.useEffect(() => {
    if (!LARGE_SCREEN) {
      navigation.setOptions({
        headerRight: () => <Button onPress={onPressEdit}>{t("Edit")}</Button>,
      });
    }
  }, [LARGE_SCREEN, navigation, onPressEdit, t]);

  const renderActionButtons = () => {
    return (
      <View style={[styles.actionButtons, { marginBottom: 10 }]}>
        <HeaderBackButton
          tintColor={theme.colors.primary}
          onPress={() => navigation.goBack()}
        />
        <Button onPress={onPressEdit}>{t("Edit")}</Button>
      </View>
    );
  };

  const renderFields = () => {
    return fields
      ?.filter(
        (field) => !field.details || field.details.includes(state.user.role),
      )
      .map((field) => {
        const rawValue = localData[field.name] ?? field.defaultValue;
        const normalizedValue =
          Array.isArray(field.options) &&
          rawValue !== null &&
          typeof rawValue !== "undefined" &&
          !field.options.includes(rawValue)
            ? field.defaultValue
            : rawValue;
        const roleHiddenValues =
          field.hideWhenValueInDisplayByRole?.[state.user.role] ?? [];
        const shouldHideByValue = [
          ...(field.hideWhenValueInDisplay ?? []),
          ...roleHiddenValues,
        ]
          .map((hiddenValue) => String(hiddenValue))
          .includes(String(normalizedValue));

        const isQuickStatusField =
          quickStatusEnabled && field.name === roleQuickFieldName;

        if (shouldHideByValue && !isQuickStatusField) {
          return null;
        }

        return (
          <View key={field.name} style={{ marginBottom: 10 }}>
            {!shouldHideByValue && (
              <DetailsDisplay
                field={field}
                value={normalizedValue}
                onChange={handleFieldChange}
                item={localData}
              />
            )}
            {isQuickStatusField && (
              <View style={styles.quickStatusContainer}>
                <SegmentedButtons
                  value={String(normalizedQuickCurrentValue ?? "")}
                  onValueChange={(newValue) => {
                    const selectedValue = quickStatusOptions.find(
                      (optionValue) => String(optionValue) === newValue,
                    );
                    const currentValue = normalizedQuickCurrentValue;

                    if (
                      typeof selectedValue !== "undefined" &&
                      selectedValue !== currentValue
                    ) {
                      handleFieldChange(roleQuickFieldName, selectedValue);
                    }
                  }}
                  buttons={quickStatusOptions.map((optionValue) => ({
                    value: String(optionValue),
                    label: t(
                      quickStatus?.labelsByValue?.[String(optionValue)] ??
                        String(optionValue),
                    ),
                  }))}
                />
              </View>
            )}
          </View>
        );
      });
  };

  return (
    <ModalScreenWrapper
      onClose={() => navigation.goBack()}
      renderActionButtons={LARGE_SCREEN ? renderActionButtons : undefined}
      modalWidth={collection.name === "delivery_orders" ? 640 : 800}
    >
      {renderFields()}
      {shouldShowActivityLogs && (
        <TaskActivityLogs
          collectionName={collection.name}
          taskId={localData.id}
          subcollectionName={activityLogsConfig.subcollection}
          limitCount={activityLogsConfig.limit}
        />
      )}
    </ModalScreenWrapper>
  );
}

const styles = StyleSheet.create({
  actionButtons: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  quickStatusContainer: {
    marginTop: 8,
  },
});
